import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ProductForm,
  ProductFormSkeleton,
  type ProductFormSubmitValues,
} from "@/components/ui/ProductForm";
import { VariantsManager } from "@/components/ui/VariantsManager";
import { useProduct } from "@/lib/useProduct";
import {
  apiFetch,
  bulkSetVariantsAnnouncement,
  bulkSetVariantsUnavailable,
  readDetailMessage,
  setProductStatus,
} from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { queryClient } from "@/lib/queryClient";
import { clearProductOrigin } from "@/lib/productNavigation";

export default function EditProduct() {
  const { t } = useTranslation();
  const { public_id } = useParams();
  const navigate = useNavigate();
  const { product, error, setProduct } = useProduct(public_id);
  usePageTitle(product ? `${t("product.editProduct")} · ${product.name}` : t("product.editProduct"));
  const [fieldsReady, setFieldsReady] = useState(false);
  const [variantCount, setVariantCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [variantAnnouncementIds, setVariantAnnouncementIds] = useState<Record<number, string | null>>({});
  const [variantUnavailableIds, setVariantUnavailableIds] = useState<Record<number, boolean>>({});
  const submitFormRef = useRef<(() => Promise<void>) | null>(null);
  const addPendingVariantRef = useRef<(() => Promise<number | null>) | null>(null);
  const shouldPublishRef = useRef(false);

  const readyToPublish = product?.status.name === "draft" && fieldsReady && variantCount > 0;

  useEffect(() => {
    if (!product) return;
    setVariantAnnouncementIds(
      Object.fromEntries(product.variants.map(variant => [variant.id, variant.announcement_id ?? null]))
    );
    setVariantUnavailableIds(
      Object.fromEntries(product.variants.map(variant => [variant.id, variant.is_unavailable ?? false]))
    );
  }, [product?.id]);

  const onSubmit = async (data: ProductFormSubmitValues) => {
    const payload = {
      name: data.name,
      name_en: data.name_en,
      description: data.description,
      description_en: data.description_en,
      category_id: data.category_id,
      brand_id: data.brand_id,
    };

    const res = await apiFetch(`/product/${public_id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(readDetailMessage(body?.detail, t("product.couldNotUpdateProduct")));
    }

    const updated = await res.json();

    const variantsByAnnouncement = Object.entries(variantAnnouncementIds).reduce(
      (groups, [variantId, announcementId]) => {
        const key = announcementId ?? "__none__";
        (groups[key] ??= []).push(Number(variantId));
        return groups;
      },
      {} as Record<string, number[]>
    );
    for (const [announcementKey, variantIds] of Object.entries(variantsByAnnouncement)) {
      await bulkSetVariantsAnnouncement(
        variantIds,
        announcementKey === "__none__" ? null : announcementKey
      );
    }

    for (const unavailable of [true, false]) {
      const variantIds = Object.entries(variantUnavailableIds)
        .filter(([, value]) => value === unavailable)
        .map(([variantId]) => Number(variantId));
      if (variantIds.length) await bulkSetVariantsUnavailable(variantIds, unavailable);
    }

    if (shouldPublishRef.current && public_id) {
      await setProductStatus(public_id, "published");
    }

    clearProductOrigin();
    await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });

    navigate(`/product/${updated.slug}/p/${updated.id}`);
  };

  const revertToDraft = async () => {
    if (!public_id) return;
    setStatusError(null);
    try {
      const updated = await setProductStatus(public_id, "draft");
      setProduct(prev => (prev ? { ...prev, status: updated.status } : prev));
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : t("product.couldNotUpdateProduct"));
    }
  };

  const handlePrimaryClick = async () => {
    setSubmitting(true);
    try {
      let count = variantCount;
      if (addPendingVariantRef.current) {
        const result = await addPendingVariantRef.current();
        if (result === null) return;
        count = result;
      }
      shouldPublishRef.current = product?.status.name === "draft" && fieldsReady && count > 0;
      await submitFormRef.current?.();
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <div>{error}</div>;
  if (!product) return <ProductFormSkeleton />;

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center gap-8 py-8">
      <div className="flex flex-col items-center gap-2 w-full">
        <h1 className="text-2xl font-bold text-brand">{t("product.editProduct")}</h1>
        <div className="flex items-center gap-2 text-sm text-app-muted">
          <span>
            {t("product.status")}:{" "}
            {product.status.name === "published" ? t("products.statusPublished") : t("products.statusDraft")}
          </span>
          {product.status.name === "published" && (
            <button type="button" className="text-brand hover:underline cursor-pointer" onClick={revertToDraft}>
              {t("product.revertToDraft")}
            </button>
          )}
        </div>
        {statusError && <span className="text-red-500 text-sm">{statusError}</span>}
        <ProductForm
          defaultValues={product}
          submitLabel={t("product.saveChanges")}
          pendingLabel={t("common.saving")}
          onSubmit={onSubmit}
          hideSubmitButton
          onFieldsReadyChange={setFieldsReady}
          onExposeSubmit={trigger => {
            submitFormRef.current = trigger;
          }}
        />
      </div>
      <VariantsManager
        productId={product.id}
        productStatus={product.status.name}
        variants={product.variants}
        onProductDeleted={() => navigate("/products")}
        onProductStatusChange={status =>
          setProduct(previous => (previous ? { ...previous, status: { name: status } } : previous))
        }
        onVariantsChange={setVariantCount}
        onExposeAddPendingVariant={trigger => {
          addPendingVariantRef.current = trigger;
        }}
        announcementIds={variantAnnouncementIds}
        unavailableIds={variantUnavailableIds}
        onUnavailableChange={(variantId, unavailable) =>
          setVariantUnavailableIds(previous => ({ ...previous, [variantId]: unavailable }))
        }
        onAnnouncementChange={(variantId, announcementId) =>
          setVariantAnnouncementIds(previous => ({
            ...previous,
            [variantId]: announcementId,
          }))
        }
        onVariantDeleted={variantId => {
          setVariantAnnouncementIds(previous => {
            const next = { ...previous };
            delete next[variantId];
            return next;
          });
          setVariantUnavailableIds(previous => {
            const next = { ...previous };
            delete next[variantId];
            return next;
          });
        }}
      />
      <button
        type="button"
        className="w-full max-w-md py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        disabled={submitting}
        onClick={handlePrimaryClick}
      >
        {submitting ? t("common.saving") : readyToPublish ? t("product.saveAndPublish") : t("common.save")}
      </button>
    </div>
  );
}
